import {
	AfterViewInit,
	Component,
	computed,
	inject,
	input,
	InputSignal,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	signal,
	Signal,
	WritableSignal
} from '@angular/core';

import { Type } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MDHeaderComponent, MDContentBoxComponent } from '@melodic-ui/components';
import { ComponentLoaderService } from '@melodic-ui/services';
import { ProfileContactInfoBoxComponent } from '../profile-info-box/boxes/profile-contact-info-box/profile-contact-info-box.component';
import { ProfileEducationInfoBoxComponent } from '../profile-info-box/boxes/profile-education-info-box/profile-education-info-box.component';
import { ProfileLocationInfoBoxComponent } from '../profile-info-box/boxes/profile-location-info-box/profile-location-info-box.component';
import { ProfileNotesInfoBoxComponent } from '../profile-info-box/boxes/profile-notes-info-box/profile-notes-info-box.component';
import { ProfilePersonalInfoBoxComponent } from '../profile-info-box/boxes/profile-personal-info-box/profile-personal-info-box.component';
import { IUser } from '../../../api/mylb/interfaces';
import { ProfileSupportDataStateService } from '../profile-support-data-state.service';
import { LoaderComponent } from '../../loader/loader.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { AuthenticationService } from '../../../auth/services/authentication.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, tap } from 'rxjs';
import { AccountStateService } from '@lb/state/account-state.service';
import { DatePipe } from '@angular/common';
import { CalendarAdvancedSyncComponent } from '../../calendar-sync/calendar-advanced-sync/calendar-advanced-sync.component';

@Component({
	selector: 'lb-profile-page',
	standalone: true,
	imports: [
		FormsModule,
		ReactiveFormsModule,
		LoaderComponent,
		MDHeaderComponent,
		MDContentBoxComponent,
		ProfileContactInfoBoxComponent,
		ProfileEducationInfoBoxComponent,
		ProfileLocationInfoBoxComponent,
		ProfileNotesInfoBoxComponent,
		ProfilePersonalInfoBoxComponent,
		CalendarAdvancedSyncComponent
	],
	providers: [ProfileSupportDataStateService],
	templateUrl: './profile-page.component.html',
	styleUrl: './profile-page.component.scss'
})
export class ProfilePageComponent implements OnInit, OnDestroy, AfterViewInit {
	private _profileSupportDateStateService: ProfileSupportDataStateService = inject(ProfileSupportDataStateService);
	private _authService = inject(AuthenticationService);
	private _componentLoaderService: ComponentLoaderService = inject(ComponentLoaderService);
	private _accountStateService = inject(AccountStateService);

	public profile: InputSignal<IUser | undefined> = input<IUser>();
	public profileUpdating: InputSignal<boolean | undefined> = input<boolean>();

	public cancel: OutputEmitterRef<void> = output<void>();
	public save: OutputEmitterRef<IUser> = output<IUser>();

	public supportDataLoaded: Signal<boolean> = this._profileSupportDateStateService.loaded;
	public init: WritableSignal<boolean> = signal(false);
	public formConfigError: WritableSignal<boolean> = signal(false);
	public formDisabled: WritableSignal<boolean> = signal(false);
	public form: FormGroup = new FormGroup({});

	public internalProfile: Signal<IUser> = computed(() => {
		if (this.profile()) {
			return this.profile() as IUser;
		}

		return this._accountStateService.user();
	});
	public internalProfileUpdating: Signal<boolean> = computed(() => {
		if (this.profileUpdating()) {
			return this.profileUpdating() as boolean;
		}

		return this._accountStateService.updating();
	});

	public isAdmin: Signal<boolean> = computed(() => this._authService.hasEntitlement('Admin:App:Access'));
	public isLearner: Signal<boolean> = computed(() => {
		const profileLearner = this.internalProfile()?.entitlements.find((e) => e.entitlementName === 'Learner:App:Access') !== undefined;

		return profileLearner || this._authService.hasEntitlement('Learner:App:Access');
	});
	public isInstructor: Signal<boolean> = computed(() => this._authService.hasEntitlement('Instructor:App:Access'));
	public isGuest: Signal<boolean> = computed(
		() =>
			this.internalProfile()?.entitlements.find(
				(e) =>
					e.entitlementName === 'Guardian:App:Access' || e.entitlementName === 'PartnerContact:App:Access' || e.entitlementName === 'Guest:App:Access'
			) !== undefined
	);

	public isSelf: Signal<boolean> = computed(() => {
		const currentUser = this._accountStateService.user();
		const profile = this.internalProfile();
		return currentUser?.userID === profile?.userID;
	});

	constructor() {
		combineLatest([toObservable(this.internalProfile), toObservable(this.internalProfileUpdating), toObservable(this.isLearner)])
			.pipe(takeUntilDestroyed())
			.subscribe(([profile, profileUpdating, isLearner]) => {
				if ([profile, profileUpdating, isLearner].filter((x) => x === undefined).length !== 0) {
					this.formConfigError.set(true);
					throw new Error('If not defaulting to logged in user, you must pass in the profile, profileUpdating and isLearner inputs.');
				}
			});
		toObservable(this.isInstructor)
			.pipe(takeUntilDestroyed())
			.subscribe((isInstructor) => {
				if (isInstructor) {
					this.disableForm();
				}
			});
	}

	ngOnInit(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(PageHeaderComponent);
		this._componentLoaderService.getContainer('banner-main-content').inputs.set({ pageTitle: `My Profile` });
	}

	ngOnDestroy(): void {
		this._componentLoaderService.getContainer('banner-main-content').component.set(null);
	}
	ngAfterViewInit(): void {
		setTimeout(() => this.init.set(true), 200);
	}

	enableForm(): void {
		this.formDisabled.set(false);
		this.form.enable();
	}

	disableForm(): void {
		this.formDisabled.set(true);
		this.form.disable();
	}

	onCancel(): void {
		this.cancel.emit();

		if (!this.profile()) {
			this._accountStateService.setAccount(this.internalProfile());
		}
	}

	onSave(): void {
		const updatedUser = {
			...this.internalProfile(),
			timeZoneID: this.form.value.timezone,
			firstName: this.form.value.firstName,
			lastName: this.form.value.lastName,
			preferredName: this.form.value.preferredName,
			dateOfBirth: this.form.value.dateOfBirth,
			email: this.form.value.personalEmail,
			phone: this.form.value.phoneNumber,
			genderID: this.form.value.gender,
			pronounID: this.form.value.pronoun,
			streetAddress: this.form.value.streetAddress,
			streetAddress2: this.form.value.streetAddress2,
			city: this.form.value.city,
			state: this.form.value.state,
			postalCode: this.form.value.postalCode,
			country: this.form.value.country,
			highSchoolGraduationYear: this.form.value.graduationYear,
			educationLevelID: this.form.value.educationLevel,
			currentSchool: this.form.value.currentSchool,
			learningNeeds: this.form.value?.learningNeeds || null,
			bio: this.form.value?.bio || null,
			userImage: this.form.value.userImage || ''
		};

		this.save.emit(updatedUser);

		if (!this.profile()) {
			this._accountStateService.updateAccount(updatedUser);
		}
	}
}
